import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Zap, ShieldCheck, Crown, Activity, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Skeleton } from "@/components/ui/skeleton";
import { WaitlistModal } from "@/components/shared/WaitlistModal";
import { UsageMeter } from "@/components/billing/UsageMeter";
import { supabase } from "@/integrations/supabase/client";

import { PLANS, getMonitorLimit, getStatusPageLimit } from "@/lib/plans-config";
import { toast } from "sonner";

export function PlanSettings() {
    const [waitlistOpen, setWaitlistOpen] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const { data: profile, isLoading } = useQuery({
        queryKey: ["profile"],
        queryFn: () => api.getProfile(),
    });

    const handleCheckout = async (plan: "pro" | "scale") => {
        setCheckoutLoading(true);
        try {
            const { data, error } = await supabase.functions.invoke("dodo-checkout", {
                body: {
                    plan,
                    billing_cycle: "monthly",
                    return_url: window.location.origin + "/settings?tab=plan"
                }
            });
            if (error) throw error;
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            }
        } catch (err) {
            console.error("Checkout failed:", err);
            toast.error("Failed to start checkout. Please try again.");
        } finally {
            setCheckoutLoading(false);
        }
    };

    const { data: badge } = useQuery({
        queryKey: ["founder_badge", profile?.id],
        queryFn: async () => {
            if (!profile?.id) return null;
            const { data } = await supabase
                .from("founder_badges" as any)
                .select("badge_id")
                .eq("user_id", profile.id)
                .maybeSingle();
            return data;
        },
        enabled: !!profile?.id
    });

    if (isLoading) return <Skeleton className="h-64 border-2 border-foreground" />;

    const isPro = profile?.is_pro || false;
    const isFounder = profile?.user_mode === "founder";
    const monitorLimit = getMonitorLimit(profile);
    const statusPageLimit = getStatusPageLimit(profile);
    const checkIntervalLabel = "60 Seconds";

    return (
        <div className="space-y-8 select-none font-mono">
            <UsageMeter />
            
            <div className="border-4 border-foreground p-6 bg-accent/20 relative overflow-hidden">
                <div className="absolute top-4 right-4 text-primary opacity-20">
                    <Zap className="h-20 w-20" />
                </div>
                <div className="relative z-10">
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Current Active Plan</p>
                    <div className="flex items-center gap-3 mb-6">
                        <h3 className="text-3xl font-black uppercase tracking-tighter text-foreground">
                            {isFounder ? "Lifetime Pro" : isPro ? "Pro" : "Free"}
                        </h3>
                        {(isPro || isFounder) && (
                            <span className="bg-primary text-primary-foreground text-[10px] font-black uppercase px-2 py-0.5 shadow-xs">Active</span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        <div className="bg-background border-2 border-foreground p-3">
                            <p className="text-[9px] font-black uppercase text-muted-foreground mb-1">Protected Apps</p>
                            <p className="text-xl font-black uppercase">{monitorLimit} Max</p>
                        </div>
                        <div className="bg-background border-2 border-foreground p-3">
                            <p className="text-[9px] font-black uppercase text-muted-foreground mb-1">Check Resolution</p>
                            <p className="text-lg font-black uppercase">{checkIntervalLabel}</p>
                        </div>
                        <div className="bg-background border-2 border-foreground p-3">
                            <p className="text-[9px] font-black uppercase text-muted-foreground mb-1">Status Pages</p>
                            <p className="text-xl font-black uppercase">{statusPageLimit} Page{statusPageLimit !== 1 ? "s" : ""}</p>
                        </div>
                    </div>

                    {!isFounder && !isPro && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Button
                                 onClick={() => handleCheckout("pro")}
                                 disabled={checkoutLoading}
                                 className="h-12 rounded-none border-2 border-foreground bg-background hover:bg-foreground hover:text-background text-foreground font-black uppercase tracking-tighter shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center justify-center gap-2"
                            >
                                <Zap className="h-4 w-4" />
                                Upgrade to Pro — $49/mo
                            </Button>
                            <Button
                                 onClick={() => handleCheckout("scale")}
                                 disabled={checkoutLoading}
                                 className="h-12 rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase tracking-tighter shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center justify-center gap-2"
                            >
                                <Crown className="h-5 w-5" />
                                Lock in Lifetime Pro — $499 Once
                            </Button>
                        </div>
                    )}

                    {isPro && !isFounder && (
                        <Button
                             onClick={() => handleCheckout("scale")}
                             disabled={checkoutLoading}
                             className="w-full h-12 rounded-none border-2 border-foreground bg-primary text-primary-foreground font-black uppercase tracking-tighter shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all flex items-center justify-center gap-2"
                        >
                            <Crown className="h-5 w-5" />
                            Lock in Lifetime Pro Deal — $499 Once
                        </Button>
                    )}

                    {badge && (
                        <div className="mt-6 border-t-2 border-dashed border-foreground/20 pt-6">
                            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2">Founder Identity Verified</p>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="bg-yellow-300 text-black border-2 border-black font-black uppercase text-xs tracking-wider px-4 py-2">
                                    FOUNDER #{badge.badge_id}
                                </div>
                                <div className="flex gap-2">
                                    <Link to={`/founders/${badge.badge_id}`}>
                                        <Button className="h-10 text-xs font-black uppercase tracking-wider border-2 border-foreground bg-background hover:bg-foreground hover:text-background text-foreground rounded-none shadow-brutal-sm">
                                            View & Share Badge Card
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <h4 className="text-xs font-black uppercase tracking-widest text-foreground">Tier Advancement Intelligence</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                    { icon: Zap, text: "5 protected apps (vs 1 Free)", sub: "Run production, staging, and side projects under separate namespaces.", badge: null },
                    { icon: ShieldCheck, text: "All integrations included", sub: "Vercel, Sentry, GitHub, Datadog, Stripe, Cloudflare, and more.", badge: null },
                    { icon: Activity, text: "Advanced Routing Rules", sub: "Map specific incident streams directly to distinct alert rooms (e.g. Stripe -> #finance).", badge: "Pro / LTD" },
                    { icon: Globe, text: "25 White-Label Status Pages", sub: "Publish client status trackers with custom domains and styled CNAME records.", badge: "Pro / LTD" },
                    { icon: Crown, text: "Lifetime Pro Badge", sub: "Own a permanent animated collectible identifier badge (e.g. Founder #042).", badge: "LTD Only" },
                    { icon: ShieldCheck, text: "Priority Founder Support", sub: "Outage alerts respond directly to Manoj's inbox. Pure human responses.", badge: "Pro / LTD" },
                ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 border-2 border-foreground bg-background hover:bg-accent transition-colors relative">
                        <item.icon className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                        <div className="space-y-1 flex-1">
                            <div className="flex items-center gap-2">
                                <p className="text-xs font-black uppercase tracking-tight">{item.text}</p>
                                {item.badge && (
                                    <span className="text-[8px] font-black uppercase px-1.5 py-0.5 bg-primary text-primary-foreground">{item.badge}</span>
                                )}
                            </div>
                            <p className="text-[10px] font-bold text-muted-foreground leading-tight">{item.sub}</p>
                        </div>
                    </div>
                ))}
            </div>

            <WaitlistModal
                open={waitlistOpen}
                onOpenChange={setWaitlistOpen}
                initialPlan="founder"
            />
        </div>
    );
}
